import { CustomLayers, JackpotTiers } from "ags/names";
import CanvasManager, { Orientation } from "slotworks/graphics/canvas/canvas-manager";
import canvasManager from "slotworks/graphics/canvas/canvas-manager";
import Layers from "slotworks/graphics/layers/layers";
import Currency from "slotworks/i18n/currency";
import SmartUIGroup from "slotworks/smart-ui/smart-ui-group";
import SmartUIManager from "slotworks/smart-ui/smart-ui-manager";
import Contract from "slotworks/utils/contracts/contract";
import { Easing, Tween } from "slotworks/utils/tween";

export class JackpotComponent {

    public rearrangeLayers = false;
    public fireworksLayer: Layers;

    protected tweenInProgress = false;
    protected layer: Layers;
    protected group: SmartUIGroup;
    protected positionModified = false;
    protected positionTweenTarget: string;
    protected animationResolve: Function = null;
    protected containerPosition: number;
    protected activeTween: Tween;
    protected duration = 1000;

    public setup() {
        this.layer = Layers.Custom.get(CustomLayers.JACKPOTS);
        if (!this.fireworksLayer) {
            this.fireworksLayer = Layers.Custom.get(CustomLayers.FIREWORKS);
        }

        CanvasManager.onOrientationChange.add((orientation: Orientation) => this.switchOrientation(orientation));

        this.group = SmartUIManager.newSmartUIGroup(this.layer, "default");
        SmartUIManager.showGroup(this.group).execute();

        this.backupElementPositions();

        this.switchOrientation(CanvasManager.orientation);
    }

    public setFireworksLayer(layer: Layers) {
        this.fireworksLayer = layer;

        this.switchOrientation(CanvasManager.orientation);
    }

    public setJackpotAmounts(options: JackpotOptions) {
        this.setJackpotAmount(JackpotTiers.GRAND, options.grand);
        this.setJackpotAmount(JackpotTiers.MAJOR, options.major);
        this.setJackpotAmount(JackpotTiers.MINI, options.mini);
        this.setJackpotAmount(JackpotTiers.MINOR, options.minor);
    }

    public animateJackpotToTarget(targetName: string) {
        return new Contract<void>((resolve) => {

            this.positionTweenTarget = targetName;

            this.animationResolve = resolve;

            const targetPosition = this.layer.getPosition(`${targetName}${canvasManager.orientation}`);
            const target = this.positionModified ? this.containerPosition : this.containerPosition + targetPosition.y;

            this.activeTween = new Tween(this.layer.container);
            this.activeTween.to({ y: target }, this.duration);
            this.activeTween.easing(Easing.Sinusoidal.InOut);
            this.activeTween.start();
            this.activeTween.onComplete(() => {
                this.cancelTweenInProgress();
            });

            this.tweenInProgress = true;
        });
    }

    protected snapJackpotToTarget(targetName: string, orientation: Orientation) {

        if (!targetName) {
            return;
        }

        const targetPosition = this.layer.getPosition(`${targetName}${orientation}`);
        this.layer.container.y = this.containerPosition + targetPosition.y;
    }

    private backupElementPositions() {
        this.containerPosition = this.layer.container.y;
    }

    private cancelTweenInProgress() {

        if (!this.tweenInProgress) {
            return;
        }

        this.activeTween.stop();
        this.tweenInProgress = false;
        this.animationResolve(null);
        this.animationResolve = null;

        this.positionModified = !this.positionModified;
    }

    private switchOrientation(orientation?: Orientation) {
        this.layer.refreshBitmapCache();

        if (!orientation) {
            orientation = CanvasManager.orientation;
        }

        this.cancelTweenInProgress();

        switch (orientation) {
            case Orientation.LANDSCAPE:
            default:
                if (this.rearrangeLayers) {
                    this.layer.moveBehind(Layers.MatrixBackground);
                    this.fireworksLayer.moveBehind(this.layer);
                }
                this.setVisible(true, orientation.toString());
                this.setVisible(false, Orientation.PORTRAIT.toString());
                this.snapJackpotToTarget(this.positionTweenTarget, Orientation.LANDSCAPE);
                break;
            case Orientation.PORTRAIT:
                if (this.rearrangeLayers) {
                    if (Layers.Bonus.getCurrentScene().name === "default") {
                        this.fireworksLayer.moveBehind(Layers.MenusContent);
                        this.layer.moveBehind(this.fireworksLayer);
                        Layers.Bonus.moveBehind(this.layer);
                    } else {
                        this.layer.moveBehind(Layers.MenusContent);
                        Layers.Bonus.moveBehind(this.layer);
                    }
                }

                this.setVisible(true, orientation.toString());
                this.setVisible(false, Orientation.LANDSCAPE.toString());
                this.snapJackpotToTarget(this.positionTweenTarget, Orientation.PORTRAIT);
                break;
        }
    }

    private setVisible(visible: boolean, orientation: string) {
        this.getDisplayElements(orientation).forEach((element) => {
            element.visible = visible;
        });
    }

    private setJackpotAmount(jackpot: JackpotTiers, amount: number) {
        const layer = Layers.Custom.get(CustomLayers.JACKPOTS);
        layer.getText("jackpot_text_" + jackpot + "_" + Orientation.PORTRAIT.toString()).text = Currency.format(amount);
        layer.getText("jackpot_text_" + jackpot + "_" + Orientation.LANDSCAPE.toString()).text = Currency.format(amount);
    }

    private getDisplayElements(orientation: string) {
        return [
            this.layer.getSprite("jackpots_bg_" + orientation),
            this.layer.getText("jackpot_text_" + JackpotTiers.GRAND + "_" + orientation),
            this.layer.getText("jackpot_text_" + JackpotTiers.MAJOR + "_" + orientation),
            this.layer.getText("jackpot_text_" + JackpotTiers.MINI + "_" + orientation),
            this.layer.getText("jackpot_text_" + JackpotTiers.MINOR + "_" + orientation),
            this.layer.getText("jackpot_label_" + JackpotTiers.GRAND + "_" + orientation),
            this.layer.getText("jackpot_label_" + JackpotTiers.MAJOR + "_" + orientation),
            this.layer.getText("jackpot_label_" + JackpotTiers.MINI + "_" + orientation),
            this.layer.getText("jackpot_label_" + JackpotTiers.MINOR + "_" + orientation)
        ];
    }
}

export default new JackpotComponent();

export class JackpotOptions {
    constructor(public grand: number, public major: number, public minor: number, public mini: number) {
        //
    }

    public getForTier(tier: JackpotTiers) {
        switch (tier) {
            case JackpotTiers.GRAND:
                return this.grand;
            case JackpotTiers.MAJOR:
                return this.major;
            case JackpotTiers.MINI:
                return this.mini;
            case JackpotTiers.MINOR:
                return this.minor;
        }
    }
}
